import { MAP_SIZE } from './fixed.ts';
import type { Domain } from './data.ts';

export const EMPTY = -1;

/**
 * The sea is the westmost column of the map: the base has the sea on one side and land on the
 * other three. The rest of the margin stays land, where attackers deploy.
 */
export const SEA_WIDTH = 1;

/**
 * The open sea beyond the coast, in tiles: a bay as wide as the map runs this far out to the west: as deep as the map is wide.
 * Surface ships appear OFFSHORE tiles out and sail in to the coast; submarines and carriers hold
 * station STANDOFF tiles out and launch from there.
 */
export const SEA_REACH = 64;
export const OFFSHORE = 30;
export const STANDOFF = 20;

/** Whether a tile is sea. Only the west edge is water. */
export function isSeaTile(x: number, _y: number): boolean {
  return x < SEA_WIDTH;
}

export function tileIndex(x: number, y: number): number {
  return y * MAP_SIZE + x;
}

export function inBounds(x: number, y: number): boolean {
  return x >= 0 && y >= 0 && x < MAP_SIZE && y < MAP_SIZE;
}

/**
 * Occupancy grid: each tile holds the id of the building standing on it, or
 * EMPTY. Walls are buildings too. noDeploy marks tiles within one tile of any
 * footprint, where units may not be dropped.
 */
export class Grid {
  readonly occ = new Int16Array(MAP_SIZE * MAP_SIZE).fill(EMPTY);
  readonly noDeploy = new Uint8Array(MAP_SIZE * MAP_SIZE);
  readonly water = new Uint8Array(MAP_SIZE * MAP_SIZE);

  constructor() {
    for (let y = 0; y < MAP_SIZE; y++) {
      for (let x = 0; x < MAP_SIZE; x++) if (isSeaTile(x, y)) this.water[tileIndex(x, y)] = 1;
    }
  }

  isWater(x: number, y: number): boolean {
    return inBounds(x, y) && this.water[tileIndex(x, y)] === 1;
  }

  place(id: number, x: number, y: number, size: number): void {
    for (let dy = 0; dy < size; dy++) {
      for (let dx = 0; dx < size; dx++) {
        if (inBounds(x + dx, y + dy)) this.occ[tileIndex(x + dx, y + dy)] = id;
      }
    }
    for (let dy = -1; dy <= size; dy++) {
      for (let dx = -1; dx <= size; dx++) {
        if (inBounds(x + dx, y + dy)) this.noDeploy[tileIndex(x + dx, y + dy)] = 1;
      }
    }
  }

  clear(x: number, y: number, size: number): void {
    for (let dy = 0; dy < size; dy++) {
      for (let dx = 0; dx < size; dx++) {
        if (inBounds(x + dx, y + dy)) this.occ[tileIndex(x + dx, y + dy)] = EMPTY;
      }
    }
  }

  at(x: number, y: number): number {
    return inBounds(x, y) ? this.occ[tileIndex(x, y)]! : -2;
  }

  /**
   * Whether a unit of this domain may be dropped on a tile. Ships need open sea,
   * ground units need dry land away from buildings, and flying units may go
   * anywhere that is not next to a building.
   */
  canDeploy(x: number, y: number, domain: Domain = 'ground'): boolean {
    if (!inBounds(x, y)) return false;
    if (domain === 'sea') return this.isWater(x, y) && this.occ[tileIndex(x, y)] === EMPTY;
    if (this.noDeploy[tileIndex(x, y)] !== 0) return false;
    return domain !== 'ground' || !this.isWater(x, y);
  }
}
